import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, ScrollView, Modal, Pressable, useWindowDimensions } from 'react-native';
import Animated, { FadeIn, FadeOut, useAnimatedStyle } from 'react-native-reanimated';
import { stroke } from 'src/theme/colors';
import { ICON_NAMES } from '../../../icon/icon.helper';
import { Icon } from '../../../icon/Icon';
import TextLato from '../../../text/text-lato/TextLato';
import { stylesDesktop, stylesMobile } from './GridItemContentHeaderV3.styles';
import { useGridItemContentHeaderLogicV3 } from './GridItemContentHeaderV3.logic';
import { GridItemContentHeaderProps } from './GridItemContentHeaderV3.types';
import { observer } from 'mobx-react-lite';
import { useResponsiveScale } from 'src/contexts/ResponsiveScaleContext';
import { STACKS_ANIMATION_MS } from 'src/constants/expandableGroup.constants';
import { useOnClickOutside } from 'src/hooks/useOnClickOutside';
import { ProductOptionsV3 } from '@photosi/rn-components/product-options-v3/ProductOptionsV3';
import { CheckBox } from '@photosi/rn-components/checkbox/CheckBox';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { getPaperI18nKey } from 'src/_shared/utils/getI18nKeys';
import { LookupDropdownItemV3 } from './LookupDropdownItemV3';
import { PressableHovered } from 'src/editor-v3/components/pressable-hovered/PressableHovered';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { productContentHeaderTestID } from 'src/helpers/testIDs.helper';
import { GROUP_CODES } from 'src/constants/code.constants';
import { FocusGroups, PRODUCT_FOCUS_ORDER_STEP } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { useFocusContext } from 'src/contexts/FocusContext';

/**
 * GridItemContentHeader Component
 *
 * This component renders a title bar with optional check icon on the left
 * and two action icons on the right with animated hover effects.
 *
 * Props:
 * - text: The title text to display
 * - edit: Function called when edit icon is clicked
 * - duplicate: Function called when duplicate icon is clicked
 * - isSelected: Whether to show the check icon
 * - isLookupOpen: Whether the lookup view is open
 *
 * Animations:
 * - Animated hover effect for icons on web (shows text above icons)
 * - FadeIn/FadeOut animations for hover text
 */
export const GridItemContentHeaderV3: React.FC<GridItemContentHeaderProps> = observer(
  ({ product, onShowOptionMobile, onSelect, onPressZoomInner, index = 0 }) => {
    const {
      i18n,
      isCheckboxHovered,
      handleCheckboxMouseEnter,
      handleCheckboxMouseLeave,
      getPaperOptions,
      applyLookupToProductGroup,
      onPressEditPersonalize,
      gridColumns,
      avoidWidthImage,
    } = useGridItemContentHeaderLogicV3(product);

    const { responsiveScaleSV } = useResponsiveScale();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isDropdownPaperOpen, setIsDropdownPaperOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left?: number; right?: number } | null>(null);
    const dropdownRef = useRef<View>(null);
    const dropdownPaperRef = useRef(null);
    const { isDesktop, isMobile, isTablet } = useResponsiveScreen();
    const styles = useMemo(() => (isDesktop ? stylesDesktop : stylesMobile), [isDesktop]);
    const showPaperOption =
      ((isMobile && gridColumns !== 2) || isTablet || isDesktop) && product?.groupCode !== GROUP_CODES.PRINT_VINTAGE;
    const paper: string = useMemo(
      () => i18n.t(getPaperI18nKey(product.family, product?.choices?.paper)),
      [product?.choices?.paper],
    );
    const { width: winWidth } = useWindowDimensions();
    useOnClickOutside([dropdownPaperRef], () => {
      setIsDropdownPaperOpen(false);
    });

    const paperFocusId = productContentHeaderTestID(product.id, TestIDs.PAPER);
    const optionsFocusId = productContentHeaderTestID(product.id, TestIDs.OPTIONS_MENU);
    const { makeOnlySomeElementGroupsFocusable, makeAllElementGroupsFocusable, focusElement } = useFocusContext();
    useEffect(() => {
      if (!isDropdownPaperOpen) return undefined;
      makeOnlySomeElementGroupsFocusable([FocusGroups.LOOKUP_CHOICE]);
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsDropdownPaperOpen(false);
          focusElement(paperFocusId);
        }
      };
      window.addEventListener('keydown', handleEscKey);
      return () => {
        makeAllElementGroupsFocusable();
        window.removeEventListener('keydown', handleEscKey);
      };
    }, [isDropdownPaperOpen]);

    const onPressZoom = () => {
      setIsDropdownOpen(false);
      setDropdownPos(null);
      onPressZoomInner();
    };

    const onPressOptions = () => {
      setIsDropdownPaperOpen(false);
      onShowOptionMobile && onShowOptionMobile();
      if (!isDropdownOpen) {
        dropdownRef.current?.measureInWindow((x, y, width, _height) => {
          setDropdownPos({ top: y + 24, right: winWidth - (x + width) + 10 });
        });
      } else {
        setDropdownPos(null);
      }
      setIsDropdownOpen(prev => !prev);
      amplitude.trackEvent(AnalyticsEvents.personalize.printOptionsClick, {
        section_name: SECTION_NAMES.OPTIONS_MENU,
      });
    };

    const focusOrderBase = index * PRODUCT_FOCUS_ORDER_STEP;

    const maxWidthAnimatedStyle = useAnimatedStyle(() => {
      return {
        width: product.preview.viewBox.width * responsiveScaleSV.value,
      };
    }, [product.preview.viewBox]);

    return (
      <Animated.View style={[styles.container, !avoidWidthImage && maxWidthAnimatedStyle]}>
        <View style={styles.leftSection}>
          <View style={styles.sectionWrapper}>
            <CheckBox
              testID={productContentHeaderTestID(product.id, TestIDs.CHECKBOX)}
              onPress={onSelect}
              isChecked={product.isSelected}
              onMouseEnter={handleCheckboxMouseEnter}
              onMouseLeave={handleCheckboxMouseLeave}
              accessibilityLabel={i18n.t(product.isSelected ? 'actions:deselect_photo' : 'actions:select_photo', {
                number: index + 1,
                count: product.quantity,
              })}
              focusGroup={FocusGroups.PRODUCT}
              focusId={productContentHeaderTestID(product.id, TestIDs.CHECKBOX)}
              focusOrder={focusOrderBase + 0}
            />
            {isCheckboxHovered && !product.isSelected && (
              <Animated.View
                entering={FadeIn.duration(200)}
                exiting={FadeOut.duration(200)}
                style={[styles.hoverTextContainer, { bottom: 26, left: '50%', transform: [{ translateX: '-50%' }] }]}
                pointerEvents="none"
              >
                <TextLato style={styles.hoverText}>{i18n.t('actions:select')}</TextLato>
              </Animated.View>
            )}
          </View>
        </View>

        {/* Right section with action icons */}
        <View style={[styles.rightSection]} ref={dropdownPaperRef}>
          {!!paper && showPaperOption && (
            <View>
              <PressableHovered
                testID={productContentHeaderTestID(product.id, TestIDs.PAPER)}
                focusId={paperFocusId}
                onPress={() => {
                  setIsDropdownPaperOpen(!isDropdownPaperOpen);
                  setIsDropdownOpen(false);
                }}
                style={styles.paperButton}
                hint={i18n.t('actions:paper')}
                hintStyle={{ bottom: 35, left: '50%', transform: [{ translateX: '-50%' }] }}
                pressed={isDropdownPaperOpen}
                accessibilityLabel={i18n.t('actions:paper')}
                accessibilityRole="button"
                accessibilityState={{ expanded: isDropdownPaperOpen }}
                focusGroup={FocusGroups.PRODUCT}
                focusOrder={focusOrderBase + 1}
              >
                <TextLato style={styles.paperText}>{paper}</TextLato>
              </PressableHovered>

              {isDropdownPaperOpen && (
                <Animated.View
                  entering={FadeIn.duration(STACKS_ANIMATION_MS / 2)}
                  exiting={FadeOut.duration(STACKS_ANIMATION_MS / 2)}
                  style={[styles.dropdownLookupContainer, { left: 'auto', right: 0 }]}
                >
                  <ScrollView style={styles.scrollViewVertical}>
                    {getPaperOptions()?.map((item, itemIndex) => {
                      return (
                        <LookupDropdownItemV3
                          key={item.productOptionChoiceCode}
                          item={item}
                          preText={i18n.t('actions:finishing')}
                          showPrice={false}
                          focusOrder={focusOrderBase + itemIndex}
                          onPress={item => {
                            setIsDropdownPaperOpen(false);
                            focusElement(paperFocusId);
                            applyLookupToProductGroup(item);
                          }}
                        />
                      );
                    })}
                  </ScrollView>
                </Animated.View>
              )}
            </View>
          )}
          <View>
            <PressableHovered
              testID={productContentHeaderTestID(product.id, TestIDs.EDIT)}
              onPress={onPressEditPersonalize}
              hint={i18n.t('actions:edit')}
              hintStyle={{ bottom: 35, left: '50%', transform: [{ translateX: '-50%' }] }}
              style={styles.selectButton}
              accessibilityLabel={i18n.t('actions:edit')}
              accessibilityRole="button"
              focusGroup={FocusGroups.PRODUCT}
              focusOrder={focusOrderBase + 2}
            >
              <Icon name={ICON_NAMES.Edit} color={stroke.strokeColorActive} height={14} width={14} />
            </PressableHovered>
          </View>
          <View ref={dropdownRef}>
            <PressableHovered
              testID={productContentHeaderTestID(product.id, TestIDs.OPTIONS_MENU)}
              focusId={optionsFocusId}
              onPress={onPressOptions}
              style={styles.selectButton}
              hint={i18n.t('actions:options')}
              hintStyle={{ bottom: 35, left: '50%', transform: [{ translateX: '-50%' }] }}
              pressed={isDropdownOpen}
              accessibilityLabel={i18n.t('actions:options')}
              accessibilityRole="button"
              accessibilityState={{ expanded: isDropdownOpen }}
              focusGroup={FocusGroups.PRODUCT}
              focusOrder={focusOrderBase + 3}
            >
              <Icon name={ICON_NAMES.More} color={stroke.strokeColorActive} filled height={14} width={14} />
            </PressableHovered>

            {isDropdownOpen && dropdownPos && (
              <Modal
                transparent
                animationType="none"
                onRequestClose={() => {
                  setIsDropdownOpen(false);
                  setDropdownPos(null);
                  focusElement(optionsFocusId);
                }}
              >
                <Pressable
                  style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                  onPress={() => {
                    setIsDropdownOpen(false);
                    setDropdownPos(null);
                  }}
                />
                <View
                  style={{
                    position: 'absolute',
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    right: dropdownPos.right,
                  }}
                >
                  <ProductOptionsV3 product={product} onPressZoomInner={onPressZoom} index={index} />
                </View>
              </Modal>
            )}
          </View>
        </View>
      </Animated.View>
    );
  },
);
